/**
 *
 * "Generates a form for rendering post shut Report."
 *
 * @file   PostShutForm.js
 * @author Lateral
 * @since  2023
 */
import {
  Box,
  Grid,
  TextField,
  Typography,
  Checkbox,
  useTheme,
  FormControlLabel,
  Stack,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Divider,
  RadioGroup,
  Radio
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
dayjs.extend(isBetween)
import {
  companyName,
  crewDetailsName,
  defaultString,
  introductionName,
  recentJobsName,
  recommendationsName,
  safetyIssuesName,
  scopeOfWorkName,
  shutdownCommentsName,
  shutEndDateName,
  shutStartDateName,
  siteContactName,
  siteName,
  siteRepresentativeName,
  titleName
} from 'pages/reporting/common/formNames'
import MaintenanceJobs from './MaintenanceJobs'
import { DropdownInput } from '../common/DropdownInput'
import { TextInput } from '../common/TextInput'
import { TextAreaInput } from '../common/TextAreaInput'
import { DatePicker } from '@mui/x-date-pickers'
import { useCurrentUser } from 'hooks'
import { SCOPES } from 'components'
import { LoadingButton } from '@mui/lab'
import { useGetUsers } from 'pages/admin/common/adminDatastore'
import { PersonnelDetails } from './PersonnelDetails'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/Add'
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/Remove'
import { DeckAccordion } from 'pages/deck/common/DeckAccordion'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'

const parseString = (text) => {
  if (!text) {
    return text
  }
  if (typeof text !== 'string') {
    return text
  }
  return parseString(JSON.parse(text))
}

function PostShutForm({
  data,
  customers,
  sites,
  locations,
  decks,
  deckRevisions,
  deckRevisionHistories,
  isCreatingReport,
  onSubmit
}) {
  /**
   * Table for viewing all Reporting history.
   *
   * @function
   *
   * @param {object} data - Site object of which Parts report is going to be generated
   * @param {string} data.DateOfVisit - Shut start date
   * @param {string} data.EndDate - Shut end date
   * @param {string} data.Company - Name of the company
   * @param {string} data.Site - Name of the site
   * @param {string} data.SiteContact - Contact details of site
   * @param {string} data.SiteRepresentative - Name f site representative
   * @param {string} data.Introduction - Report introduction
   * @param {string} data.ScopeOfWork - Report scope pf work
   * @param {string} data.SafetyIssues - Reproted safety issue
   * @param {string} data.CrewDetails - Details of reporting crew
   * @param {string} data.ShutdownComments - Comments shutdown report
   * @param {string} data.Recommendations - Recommendation on visiting
   * @param {object[]} customers - Array of all company object
   * @param {object[]} location - Array of all location object
   * @param {object[]} sites - Array of all site object
   * @param {object[]} decks - Array of all deck location object
   * @param {object[]} deckRevisions - Array of deck rivision records
   * @param {object[]} deckRevisionHistories - array of deck maintenance objects
   * @param {} onSubmit - Action on submitting form
   *
   * @returns {object} - Post shut report form
   */
  const defaultValues = {
    id: data?.id ?? defaultString,
    [shutStartDateName]: data ? dayjs(data.DateOfVisit) : dayjs(),
    [shutEndDateName]: data ? dayjs(data.EndDate) : dayjs(),
    [companyName]: customers.find((c) => c.Name === data?.Company)?.id ?? defaultString,
    [siteName]: sites.find((s) => s.Name === data?.Site)?.id ?? defaultString,
    [siteContactName]: Array.isArray(data?.SiteContact)
      ? data?.SiteContact
      : typeof data?.SiteContact === 'string' && data?.SiteContact?.trim()?.length
      ? parseString(data?.SiteContact)
      : [],
    [siteRepresentativeName]: Array.isArray(data?.SiteRepresentative)
      ? data?.SiteRepresentative
      : typeof data?.SiteRepresentative === 'string' && data?.SiteRepresentative?.trim()?.length
      ? parseString(data?.SiteRepresentative)
      : [],
    [introductionName]: data?.Introduction ?? defaultString,
    [scopeOfWorkName]: data?.ScopeOfWork ?? defaultString,
    [safetyIssuesName]: data?.SafetyIssues ?? defaultString,
    [crewDetailsName]: data?.CrewDetails ?? [],
    [shutdownCommentsName]: data?.ShutdownComments ?? defaultString,
    [recommendationsName]: data?.Recommendations ?? defaultString,
    [titleName]: data?.Title ?? defaultString,
    [recentJobsName]: [],
    includeRecommendation: data?.includeRecommendation || false,
    includeShutDownComments: data?.includeShutDownComments || false,
    itemizedBOM: data?.itemizedBOM || false,
    wearReport: '%'
  }

  const {
    control,
    watch,
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({ defaultValues })

  const { fields, append, remove } = useFieldArray({
    control,
    name: recentJobsName
  })

  const crewDetails = useFieldArray({
    control,
    name: crewDetailsName
  })

  const siteContacts = useFieldArray({
    control,
    name: siteContactName
  })

  const sandvikSiteReps = useFieldArray({
    control,
    name: siteRepresentativeName
  })

  const watchCompany = watch(companyName, defaultString)
  const watchSite = watch(siteName, defaultString)
  const watchStartDate = watch(shutStartDateName, data ? dayjs(data.DateOfVisit) : dayjs())
  const watchEndDate = watch(shutEndDateName, data ? dayjs(data.EndDate) : dayjs())
  const showShutDownComments = watch('includeShutDownComments', data ? data.includeShutDownComments : true)
  const showRecommendation = watch('includeRecommendation', data ? data.includeRecommendation : true)
  const watchFields = watch(recentJobsName)
  const watchWearReportType = watch('wearReport', '%')

  const { setCurrentMaintenanceView } = useCurrentDeckContext()

  useEffect(() => {
    setCurrentMaintenanceView(watchWearReportType)
  }, [watchWearReportType])

  const { currentUser } = useCurrentUser()

  const { usersQuery } = useGetUsers(currentUser.accessToken)

  const sandvikUsers = useMemo(() => {
    if (!usersQuery.data) {
      return []
    }

    return usersQuery.data
      .filter((item) => {
        return item?.Attributes?.find((x) => x.Name === 'email')?.Value?.includes('sandvik.com')
      })
      .map((item) => {
        const sandvikId = item.Attributes?.find((x) => x.Name === 'sandvikUserId')?.Value

        return {
          id: sandvikId,
          email: item?.Attributes?.find((x) => x.Name === 'email')?.Value,
          name: `${item?.Attributes?.find((x) => x.Name === 'given_name')?.Value} ${
            item?.Attributes?.find((x) => x.Name === 'family_name')?.Value
          }`
        }
      })
  }, [usersQuery.data])

  const companyIdToFilter = watchCompany.length ? watchCompany : defaultValues[companyName]
  const siteIdToFilter = watchSite.length ? watchSite : defaultValues[siteName]
  const sitesAvailable = currentUser.getSitesForScope(SCOPES.reporting.Write)
  const companiesAvailable = currentUser.getCustomersForScope(SCOPES.reporting.Write)
  const filteredCompanies = currentUser.isAdmin ? customers : customers.filter((c) => companiesAvailable.includes(c.id))
  const theme = useTheme()
  useEffect(() => {
    if (data && Object.entries(data).length && !fields.length && siteIdToFilter) {
      onFillRecentMaintenanceJobs()
    }
  }, [])

  const [isLoading, setIsLoading] = useState()

  //Maintenance is only rendered once a company/site/time are selected.
  function onFillRecentMaintenanceJobs(value, name) {
    const siteId = name === siteName ? value : siteIdToFilter
    const startDate = dayjs(name === shutStartDateName ? value : watchStartDate).startOf('date')
    const endDate = dayjs(name === shutEndDateName ? value : watchEndDate).endOf('date')

    if (siteId && startDate && endDate) {
      remove()
      const siteLocations = locations.filter((l) => l.SiteId === siteId)

      siteLocations.forEach((location) => {
        const locationDecks = decks.filter((d) => d.LocationId === location.id && d.CustomerId === companyIdToFilter)

        locationDecks.forEach((deck) => {
          const deckDeckRevisions = deckRevisions.filter((d) => d.DeckId === deck.id)

          deckDeckRevisions.forEach((revision) => {
            const revisionHistories = deckRevisionHistories.filter(
              (d) =>
                d.RevisionId === revision.id &&
                d.HistoryType === 'Maintenance' &&
                dayjs(d.DatePerformed).isBetween(startDate, endDate)
            )

            revisionHistories.forEach((history) => {
              append({
                HistoryId: history.id,
                Name: `${location.Name} - ${deck.DeckHeader.EquipmentId} - ${deck.DeckHeader.DeckLevel}`,
                Include: data.Selections?.includes(history.id) ?? false,
                DatePerformed: history.DatePerformed
              })
            })
          })
        })
      })
    }
  }

  // Form submission event
  async function submitForm(form) {
    try {
      setIsLoading(true)
      form[recentJobsName].forEach((job) => {
        const canvas = document.getElementById(job.HistoryId)
        job.htmlElement = canvas
        const preCanvas = document.getElementById(`${job.HistoryId}-pre`)
        job.preElement = preCanvas
        const defaultCanvas = document.getElementById(`${job.HistoryId}-default`)
        job.defaultElement = defaultCanvas
      })
      await onSubmit(form)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Paper
        sx={{
          height: '100%',
          width: '70%',
          overflowY: 'scroll'
        }}>
        <Box
          width="100%"
          sx={{
            color: 'supporting.pale',
            backgroundColor: 'primary.contrastText',
            padding: '1em'
          }}>
          <Typography variant="h4" mb={1} component="h4">
            Post-Shut Report
          </Typography>
          <Typography variant="h5" component="h5">
            Report Details
          </Typography>
        </Box>
        <form id="post-shut" onSubmit={(e) => handleSubmit(submitForm)(e)}>
          <Box sx={{ padding: '0.5em 1em' }}>
            <Grid container alignItems="center">
              <Grid item xs={12} md={3} sx={{ marginTop: '0.5em' }}>
                <Typography variant="h5" component="h5">
                  {shutStartDateName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={9} sx={{ marginBottom: '0.5em' }}>
                <Controller
                  control={control}
                  name={shutStartDateName}
                  render={({ field: { onChange, ...otherFields } }) => (
                    <DatePicker
                      {...otherFields}
                      maxDate={watchEndDate}
                      onChange={(e) => {
                        onChange(e)
                        onFillRecentMaintenanceJobs(e, shutStartDateName)
                      }}
                      renderInput={(params) => (
                        <TextField color="secondary" sx={{ width: '50%' }} fullWidth {...params} />
                      )}
                      format="DD/MM/YYYY"
                    />
                  )}
                />
                {errors[shutStartDateName] && <p>This field is required</p>}
              </Grid>
            </Grid>
            <Grid container alignItems="center">
              <Grid item xs={12} md={3} sx={{ marginTop: '0.5em' }}>
                <Typography variant="h5" component="h5">
                  {shutEndDateName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={9} sx={{ marginBottom: '0.5em' }}>
                <Controller
                  control={control}
                  name={shutEndDateName}
                  render={({ field: { onChange, ...otherFields } }) => (
                    <DatePicker
                      {...otherFields}
                      minDate={watchStartDate}
                      onChange={(e) => {
                        onChange(e)
                        onFillRecentMaintenanceJobs(e, shutEndDateName)
                      }}
                      renderInput={(params) => (
                        <TextField color="secondary" sx={{ width: '50%' }} fullWidth {...params} />
                      )}
                      format="DD/MM/YYYY"
                    />
                  )}
                />
                {errors[shutEndDateName] && <p>This field is required</p>}
              </Grid>
            </Grid>
            <DropdownInput
              name={companyName}
              options={filteredCompanies.map((c) => {
                return { id: c.id, Name: c.Name }
              })}
              onDropDownChange={() => {
                remove()
                setValue(siteName, defaultString)
              }}
              control={control}
              errors={errors}
              textLabel="Select company"
            />
            <DropdownInput
              name={siteName}
              options={
                companyIdToFilter
                  ? sites
                      .filter(
                        (s) =>
                          s.CustomerId === companyIdToFilter && (currentUser.isAdmin || sitesAvailable.includes(s.id))
                      )
                      .map((c) => {
                        return { id: c.id, Name: c.Name }
                      })
                  : []
              }
              onDropDownChange={(_, name, value) => onFillRecentMaintenanceJobs(value, siteName)}
              control={control}
              errors={errors}
              textLabel="Select site"
            />

            <Stack direction="column" spacing={1} sx={{ marginBottom: '0.5em' }}>
              <Typography variant="h5" component="h5" sx={{ margin: '0.5em 0 0.2em' }}>
                {siteContactName}
              </Typography>
              {siteContacts.fields.map((crew, i) => {
                return (
                  <Stack key={crew.id} direction="row" spacing={2}>
                    <Controller
                      control={control}
                      name={`${siteContactName}.${i}.Shift`}
                      render={({ field }) => (
                        <Select id="Shift" sx={{ width: '50%' }} {...field}>
                          <MenuItem value="Day">Day</MenuItem>
                          <MenuItem value="Night">Night</MenuItem>
                        </Select>
                      )}
                    />
                    <TextField
                      label="Name"
                      placeholder="e.g. Bob Bobson"
                      color="secondary"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      {...register(`${siteContactName}.${i}.Name`, { required: true })}
                    />
                    <TextField
                      label="Position"
                      placeholder="e.g. Supervisor"
                      color="secondary"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      {...register(`${siteContactName}.${i}.Position`, { required: true })}
                    />
                    <IconButton color="secondary" variant="text" onClick={() => siteContacts.remove(i)}>
                      <RemoveCircleOutlineOutlinedIcon />
                    </IconButton>
                  </Stack>
                )
              })}
              <Box>
                <IconButton
                  color="secondary"
                  variant="text"
                  onClick={() => siteContacts.append({ Shift: 'Day', Name: '', Position: '' })}>
                  <AddCircleOutlineOutlinedIcon />
                </IconButton>
              </Box>
            </Stack>

            <Divider
              sx={{
                marginBottom: '1em'
              }}
              variant="middle"
              light
            />
            <PersonnelDetails
              control={control}
              isLoading={usersQuery.isLoading}
              label={siteRepresentativeName}
              onAddPressed={() => sandvikSiteReps.append({ Shift: 'Day', Name: '', Position: '' })}
              onRemove={(i) => sandvikSiteReps.remove(i)}
              register={register}
              textLabel="Select Sandvik site representative"
              users={sandvikUsers}
              items={sandvikSiteReps.fields}
            />

            <Divider
              sx={{
                marginBottom: '1em'
              }}
              variant="middle"
              light
            />
            <TextInput name={titleName} register={register} errors={errors} />

            <Divider
              sx={{
                marginTop: '1em'
              }}
              variant="middle"
              light
            />

            <Controller
              control={control}
              name="itemizedBOM"
              render={({ field }) => {
                return (
                  <FormControlLabel
                    control={
                      <Checkbox size="small" sx={{ color: `${theme.palette.supporting.highlight}` }} {...field} />
                    }
                    label="Itemized BOM format"
                  />
                )
              }}
            />
            <Grid container alignItems="center">
              <Grid item xs={12} md={3} sx={{ marginTop: '0.5em' }}>
                <Typography variant="h5" component="h5">
                  Wear Report
                </Typography>
              </Grid>
              <Controller
                control={control}
                name="wearReport"
                render={({ field }) => {
                  return (
                    <RadioGroup aria-labelledby="wear-report" row {...field}>
                      <FormControlLabel value="%" control={<Radio />} label="% remaining" />
                      <FormControlLabel value="mm" control={<Radio />} label="mm remaining" />
                    </RadioGroup>
                  )
                }}
              />
            </Grid>
          </Box>

          <Box p={'1em'} mb={1}>
            <DeckAccordion defaultExpanded title="Scope of Work" showTooltip={false}>
              <Box sx={{ padding: '0.5em 1em' }}>
                <TextAreaInput name={introductionName} register={register} errors={errors} />
                <TextAreaInput name={scopeOfWorkName} register={register} errors={errors} />
                <TextAreaInput name={safetyIssuesName} register={register} errors={errors} />

                <Stack direction="column" spacing={1} sx={{ marginBottom: '0.5em' }}>
                  <Typography variant="h5" component="h5" sx={{ margin: '0.5em 0 0.2em' }}>
                    {crewDetailsName}
                  </Typography>
                  {crewDetails.fields.map((crew, i) => {
                    return (
                      <Stack key={crew.id} direction="row" spacing={2}>
                        <Controller
                          control={control}
                          name={`${crewDetailsName}.${i}.Shift`}
                          render={({ field }) => (
                            <Select id="Shift" sx={{ width: '50%' }} {...field}>
                              <MenuItem value="Day">Day</MenuItem>
                              <MenuItem value="Night">Night</MenuItem>
                            </Select>
                          )}
                        />
                        <TextField
                          label="Name"
                          placeholder="e.g. Bob Bobson"
                          color="secondary"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          {...register(`${crewDetailsName}.${i}.Name`, { required: true })}
                        />
                        <TextField
                          label="Position"
                          placeholder="e.g. Supervisor"
                          color="secondary"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          {...register(`${crewDetailsName}.${i}.Position`, { required: true })}
                        />
                        <IconButton color="secondary" variant="text" onClick={() => crewDetails.remove(i)}>
                          <RemoveCircleOutlineOutlinedIcon />
                        </IconButton>
                      </Stack>
                    )
                  })}
                  <Box>
                    <IconButton
                      color="secondary"
                      variant="text"
                      onClick={() => crewDetails.append({ Shift: 'Day', Name: '', Position: '' })}>
                      <AddCircleOutlineOutlinedIcon />
                    </IconButton>
                  </Box>
                </Stack>

                <Controller
                  control={control}
                  name={`includeShutDownComments`}
                  render={({ field: { value, onChange, ...field } }) => (
                    <Box display="flex" flexDirection="column">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={value}
                            size="small"
                            onChange={onChange}
                            sx={{ color: `${theme.palette.supporting.highlight}` }}
                            {...field}
                          />
                        }
                        label={shutdownCommentsName}
                      />

                      <TextAreaInput
                        name={shutdownCommentsName}
                        includePlaceholder={false}
                        register={register}
                        errors={errors}
                        disabled={!showShutDownComments}
                      />
                    </Box>
                  )}
                />

                <Controller
                  control={control}
                  name={`includeRecommendation`}
                  render={({ field: { value, onChange, ...field } }) => (
                    <Box display="flex" flexDirection="column">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={value}
                            size="small"
                            onChange={onChange}
                            sx={{ color: `${theme.palette.supporting.highlight}` }}
                            {...field}
                          />
                        }
                        label={recommendationsName}
                      />

                      <TextAreaInput
                        name={recommendationsName}
                        includePlaceholder={false}
                        register={register}
                        errors={errors}
                        disabled={!showRecommendation}
                      />
                    </Box>
                  )}
                />
                {/* <TextAreaInput name={shutdownCommentsName} register={register} errors={errors} />
          <TextAreaInput name={recommendationsName} register={register} errors={errors} /> */}
              </Box>
            </DeckAccordion>
          </Box>

          {fields.length === 0 && (
            <Box pl="1em" pr="1em">
              <Typography color="error" sx={{ marginBottom: '.4em' }}>
                {watchSite ? 'No maintenance carried out during this timeframe.' : 'Please select a Company and Site.'}
              </Typography>
            </Box>
          )}
          <Box p={'1em'}>
            <DeckAccordion
              defaultExpanded
              title="Recent Maintenance Jobs"
              tooltipTitle="Select the maintenance activities that you wish to include in the report for the given date range above">
              <Box
                sx={{
                  padding: '1em'
                }}>
                <MaintenanceJobs
                  deckRevisions={deckRevisions}
                  deckRevisionHistories={deckRevisionHistories}
                  fields={fields}
                  control={control}
                  watchSite={watchSite}
                  isLoading={isLoading}
                />
              </Box>
            </DeckAccordion>
          </Box>
        </form>
      </Paper>

      <Box display="flex" width="70%" justifyContent="flex-end">
        <LoadingButton
          size="medium"
          form="post-shut"
          loading={isLoading || isCreatingReport}
          type="submit"
          variant="contained"
          color="secondary"
          sx={{
            marginTop: '.5em',
            marginBottom: '.5em',
            padding: '0.5em 1em'
          }}
          disabled={watchFields.filter((field) => field.Include).length === 0}>
          Generate Report
        </LoadingButton>
      </Box>
    </>
  )
}
export default PostShutForm
